import { InterpreterFrom } from 'xstate';
import { useActor } from '@xstate/react';
import classNames from 'classnames';
import { NavbarBrandLogo, NavbarIconImage } from '@typedream/core';
import { menuOptionsMachine } from '../../../../nodes/navbar/fsm/MenuOptions.fsm';
import { SelectMedia } from '../../../../components/SelectMedia';
import navbarStyles from '../../../Navbar/Navbar.module.css';
import { useTPlateEditorRef } from '../../../../plate';
import { useOutsideEventWithCanvas } from '../../../../utils/eventAlerter';

type NavbarBrandOverlayToolbarProps = {
  machineActor: InterpreterFrom<typeof menuOptionsMachine>
};

export const logoSizes = [
  {
    key: 'Small',
    scale: 0.5,
  },
  {
    key: 'Medium',
    scale: 1,
  },
  {
    key: 'Large',
    scale: 1.5,
  },
];

export function NavbarBrandOverlayToolbar({
  machineActor,
}: NavbarBrandOverlayToolbarProps) {
  const editor = useTPlateEditorRef();
  const [machineState, machineSend] = useActor(machineActor);

  const outsideDetectRef = useOutsideEventWithCanvas<HTMLDivElement>('click', () => {
    machineSend({ type: 'ON_SETTING_MENU_BLUR' });
  });

  return (
    <div
      ref={outsideDetectRef}
      style={{
        pointerEvents: 'auto',
      }}
    >
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingLeft: '5px',
            paddingRight: '5px',
            paddingTop: '5px',
          }}
        >
          <div
            style={{
              color: 'var(--text-lighter)',
            }}
          >
            Size
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {logoSizes.map((logoSize) => (
              <div
                key={logoSize.key}
                className={classNames(navbarStyles.navbarOptionsItemText, {
                  [navbarStyles.navbarOptionsItemTextActive]:
                    (machineState.context?.dataNavbar?.logoScale || 1) === logoSize.scale,
                })}
                role="none"
                onClick={() => {
                  machineSend({
                    type: 'ON_CHANGE_LOGO_SIZE',
                    newLogoScale: logoSize.scale,
                  });
                }}
                data-testid={`NavbarBrand_LogoConfig_Size_${logoSize.key}`}
              >
                {logoSize.key}
              </div>
            ))}
          </div>
        </div>
        <hr style={{ marginTop: '10px', marginBottom: '10px' }} />
      </div>

      <SelectMedia
        tabs={{
          upload: {
            accept: 'image/*',
            button: 'Choose an image',
            onSelect: async (file) => {
              if (!editor.fileManager) return;
              machineSend({ type: 'ON_UPLOAD_IMAGE', newFile: file });
            },
            disabled: machineState.hasTag('ui:show_loading_upload_image'),
          },
          // upload
          icon: {
            onSelect: (icon) => {
              const newLogo: NavbarBrandLogo = { logoType: 'icon', logoImage: icon as NavbarIconImage };
              machineSend({ type: 'ON_CHANGE_LOGO', newLogo });
            },
          },
          emoji: {
            onSelect: (emoji) => {
              const newLogo: NavbarBrandLogo = { logoType: 'emoji', logoImage: emoji };
              machineSend({ type: 'ON_CHANGE_LOGO', newLogo });
            },
          },
          link: {
            button: 'Use this image',
            placeholder: 'https://some-image-url.com/your-image',
            defaultValue:
              machineState?.context?.dataNavbar?.logoType === 'image'
                ? machineState?.context?.dataNavbar?.logoImage
                : undefined,
            onSelect: (url) => {
              const newLogo: NavbarBrandLogo = { logoType: 'image', logoImage: url };
              machineSend({ type: 'ON_CHANGE_LOGO', newLogo });
            },
          },
        }}
      />
    </div>
  );
}
